import { Controller } from "@hotwired/stimulus";

/**
 * Image Upload Controller
 * 
 * A Stimulus controller for handling image uploads and deletions.
 * Works with multiple image types (profile_picture, background_image, banner_image).
 * 
 * Targets:
 * - input: The file input element
 * - preview: The image preview container
 * - previewImage: The image element inside the preview container
 * - fileName: The element displaying the file name
 * - removeButton: The button to remove the image
 * 
 * Values:
 * - imageType: The type of image (profile_picture, background_image, banner_image)
 * - apilinkId: The ID of the apilink
 * - csrfToken: The CSRF token
 */
export default class extends Controller {
  static targets = [
    "input", 
    "preview", 
    "previewImage", 
    "fileName", 
    "removeButton"
  ];
  
  static values = {
    imageType: String,
    apilinkId: Number,
    csrfToken: String
  };

  connect() {
    console.log(`Image Upload Controller connected for ${this.imageTypeValue}`);
  }

  /**
   * Handle file selection
   * @param {Event} event - The change event from the file input
   */
  fileSelected(event) {
    const file = event.currentTarget.files[0];
    if (!file) return;
    
    // Update file name display
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name;
    }
    
    // Show preview of the selected image
    this.previewFile(file);
    
    // Upload the file
    this.uploadFile(file);
  }
  
  /**
   * Preview the selected file
   * @param {File} file - The selected file
   */
  previewFile(file) {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      if (this.hasPreviewImageTarget) {
        // Update existing preview image
        this.previewImageTarget.src = e.target.result;
      } else if (this.hasPreviewTarget) {
        // Create new preview image
        this.clearPreviewContainer();
        
        const img = document.createElement('img');
        img.src = e.target.result;
        img.className = 'w-full h-full object-cover';
        img.setAttribute('data-image-upload-target', 'previewImage');
        
        this.previewTarget.appendChild(img);
      }
    };
    
    reader.readAsDataURL(file);
  }
  
  /**
   * Clear the preview container
   */
  clearPreviewContainer() {
    if (this.hasPreviewTarget) {
      while (this.previewTarget.firstChild) {
        this.previewTarget.removeChild(this.previewTarget.firstChild);
      }
    }
  }
  
  /**
   * Upload the file to the server
   * @param {File} file - The file to upload
   */
  uploadFile(file) {
    // Show loading indicator
    this.showLoadingIndicator();
    
    // Create form data
    const formData = new FormData();
    formData.append(`apilink[${this.imageTypeValue}]`, file);
    
    // Construct the URL directly without locale prefix
    const url = `${window.location.origin}/apilinks/${this.apilinkIdValue}/autosave`;
    
    console.log('Upload URL (direct construction):', url);
    
    // Send the request
    fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': this.csrfTokenValue
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      // Hide loading indicator
      this.hideLoadingIndicator();
      
      if (data.success) {
        this.showSuccessMessage('Image enregistrée avec succès');
        
        // Force reload the entire page to get the updated image
        // This is the most reliable way to ensure the image is updated
        window.location.reload();
      } else {
        console.error('Erreur lors de la sauvegarde:', data.errors);
        this.showErrorMessage(`Erreur lors de la sauvegarde: ${data.errors.join(', ')}`);
      }
    })
    .catch(error => {
      console.error('Erreur:', error);
      this.hideLoadingIndicator();
      this.showErrorMessage('Une erreur est survenue lors de la sauvegarde');
    });
  }
  
  /**
   * Remove the image
   * @param {Event} event - The click event from the remove button
   */
  removeImage(event) {
    // Prevent the default action and stop propagation to avoid any navigation or parent handlers
    event.preventDefault();
    event.stopPropagation();
    
    // Log the event for debugging
    console.log('Remove button clicked', event);
    
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }
    
    const apilinkId = this.apilinkIdValue;
    const imageType = this.imageTypeValue;
    
    // Construct the URL directly without locale prefix
    const url = `${window.location.origin}/apilinks/${apilinkId}/destroy_${imageType}`;
    
    console.log('Delete URL (direct construction):', url);
    
    // Send the request with explicit JSON format
    fetch(`${url}?format=json`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': this.csrfTokenValue,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
    .then(response => {
      console.log('Response status:', response.status);
      if (response.ok) {
        this.showSuccessMessage('Image supprimée avec succès');
        
        // Force reload the entire page to update the UI
        // This is the most reliable way to ensure the image is updated
        window.location.reload();
      } else {
        this.showErrorMessage('Erreur lors de la suppression de l\'image');
      }
    })
    .catch(error => {
      console.error('Erreur:', error);
      this.showErrorMessage('Une erreur est survenue lors de la suppression de l\'image');
    });
    
    // Return false to prevent any other handlers from executing
    return false;
  }
  
  /**
   * Update the UI after image removal
   */
  updateUIAfterRemoval() {
    // Clear the preview container
    this.clearPreviewContainer();
    
    // Add the default SVG icon
    if (this.hasPreviewTarget) {
      const svgIcon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svgIcon.setAttribute("class", "h-10 w-10 text-gray-400");
      svgIcon.setAttribute("fill", "none");
      svgIcon.setAttribute("viewBox", "0 0 24 24");
      svgIcon.setAttribute("stroke", "currentColor");
      
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      path.setAttribute("stroke-width", "2");
      path.setAttribute("d", "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z");
      
      svgIcon.appendChild(path);
      this.previewTarget.appendChild(svgIcon);
    }
    
    // Update the file name
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = "Aucun fichier";
    }
    
    // Hide the remove button
    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.style.display = 'none';
    }
  }
  
  /**
   * Show a loading indicator
   */
  showLoadingIndicator() {
    if (this.hasPreviewTarget) {
      // Set position relative if not already
      if (this.previewTarget.style.position !== 'relative') {
        this.previewTarget.style.position = 'relative';
      }
      
      // Create loading indicator
      const loadingIndicator = document.createElement('div');
      loadingIndicator.className = 'absolute inset-0 flex items-center justify-center bg-white bg-opacity-75';
      loadingIndicator.innerHTML = '<div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>';
      loadingIndicator.id = 'loading-indicator';
      
      this.previewTarget.appendChild(loadingIndicator);
    }
  }
  
  /**
   * Hide the loading indicator
   */
  hideLoadingIndicator() {
    const indicator = document.getElementById('loading-indicator');
    if (indicator) {
      indicator.remove();
    }
  }
  
  /**
   * Show a success message
   * @param {string} message - The message to display
   */
  showSuccessMessage(message) {
    this.showMessage(message, 'bg-green-100 border-green-400 text-green-700');
  }
  
  /**
   * Show an error message
   * @param {string} message - The message to display
   */
  showErrorMessage(message) {
    this.showMessage(message, 'bg-red-100 border-red-400 text-red-700');
  }
  
  /**
   * Show a message with the specified classes
   * @param {string} message - The message to display
   * @param {string} classes - The CSS classes for the message
   */
  showMessage(message, classes) {
    const messageElement = document.createElement('div');
    messageElement.className = `fixed top-4 right-4 px-4 py-3 rounded border ${classes}`;
    messageElement.innerHTML = message;
    document.body.appendChild(messageElement);
    
    setTimeout(() => {
      messageElement.remove();
    }, 3000);
  }
  
  /**
   * Reload the preview image from the server
   */
  reloadPreviewImage() {
    if (this.hasPreviewImageTarget) {
      const currentSrc = this.previewImageTarget.src;
      
      // Don't add timestamp to data URLs (they start with "data:")
      if (currentSrc.startsWith('data:')) {
        console.log('Image is a data URL, skipping reload');
        return;
      }
      
      // For regular URLs, add a timestamp to force reload
      const timestamp = new Date().getTime();
      const baseUrl = currentSrc.split('?')[0];
      
      // Log for debugging
      console.log('Reloading image from URL:', baseUrl);
      
      // Set a small timeout to ensure the image has been processed on the server
      setTimeout(() => {
        this.previewImageTarget.src = `${baseUrl}?t=${timestamp}`;
      }, 500);
    }
  }
  
  /**
   * Get the base path with locale
   * @returns {string} The base path with locale
   */
  getBasePath() {
    const currentPath = window.location.pathname;
    console.log('Current path:', currentPath);
    
    // Extract only the locale part, ignoring everything else
    let basePath = '';
    if (currentPath.match(/^\/fr\//)) {
      basePath = '/fr';
    } else if (currentPath.match(/^\/en\//)) {
      basePath = '/en';
    }
    
    console.log('Base path:', basePath);
    return basePath;
  }
  
  /**
   * Get the full URL for an API endpoint
   * @param {string} endpoint - The API endpoint
   * @returns {string} The full URL
   */
  getApiUrl(endpoint) {
    // Use window.location.origin to get the base URL of the site
    const origin = window.location.origin;
    const basePath = this.getBasePath();
    
    // Construct the full URL
    const url = `${origin}${basePath}${endpoint}`;
    console.log('API URL:', url);
    
    return url;
  }
}
